import React, { useState } from 'react';
import axios from 'axios';
import { MapPin, Phone, Mail, Globe } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ContactSection = () => {
  const [formData, setFormData] = useState({
    company_name: '',
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    business_type: 'home',
    job_title: '',
    comments: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      // Try primary backend first
      await axios.post(`${API}/contact`, formData);
      setSubmitMessage('Thank you! We\'ll contact you soon.');
      setFormData({
        company_name: '',
        first_name: '',
        last_name: '',
        phone: '',
        email: '',
        business_type: 'home',
        job_title: '',
        comments: ''
      });
    } catch (error) {
      // Fallback: send directly via FormSubmit.co free email service
      try {
        await axios.post('https://formsubmit.co/ajax/sales@3dmetalhomebuilding.com', {
          name: `${formData.first_name} ${formData.last_name}`,
          email: formData.email,
          phone: formData.phone,
          company: formData.company_name,
          business_type: formData.business_type,
          job_title: formData.job_title,
          comments: formData.comments,
          _subject: `New Contact Form: ${formData.first_name} ${formData.last_name} - ${formData.company_name}`
        });
        setSubmitMessage('Thank you! We\'ll contact you soon.');
        setFormData({
          company_name: '',
          first_name: '',
          last_name: '',
          phone: '',
          email: '',
          business_type: 'home',
          job_title: '',
          comments: ''
        });
      } catch (fallbackError) {
        setSubmitMessage('Error submitting form. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
    {/* PDF Download Section */}
    <section className="py-16 bg-[#020617]" data-testid="pdf-download-section">
      <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
        <a
          href="https://customer-assets.emergentagent.com/job_8b3c17f2-a2f6-4d58-a7d5-815fa115249f/artifacts/ookwu1oe_3D%20Metal%20TOBY%20%201Trusses.pdf"
          target="_blank"
          rel="noopener noreferrer"
          download
          className="inline-block glass-card p-6 md:p-8 hover:border-orange-500 transition-all duration-300 cursor-pointer group"
          data-testid="pdf-download-link"
        >
          <div className="flex items-center justify-center space-x-4">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 text-orange-500 group-hover:scale-110 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <div className="text-left">
              <h3
                className="text-2xl md:text-3xl font-semibold uppercase text-orange-500"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                Download Our Brochure
              </h3>
              <p className="text-slate-300 text-base md:text-lg mt-1">
                3D Metal Home Building – Steel Trusses & Framing Technology (PDF)
              </p>
            </div>
          </div>
        </a>
      </div>
    </section>

    <section id="contact" className="py-24 md:py-32 bg-[#020617]" data-testid="contact-section">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="text-center mb-16">
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight mb-4 uppercase"
            style={{ fontFamily: 'Rajdhani, sans-serif' }}
            data-testid="contact-title"
          >
            Contact Us
          </h2>
          <p className="text-xl leading-relaxed text-slate-300" data-testid="contact-description">
            Ready to start your steel building project? Get in touch with our team.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <div className="glass-card p-8" data-testid="contact-form">
            <h3
              className="text-2xl font-semibold mb-6 uppercase text-orange-500"
              style={{ fontFamily: 'Rajdhani, sans-serif' }}
            >
              Contact Info
            </h3>
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-base font-medium text-slate-300 mb-2">Company Name *</label>
                <input
                  type="text"
                  name="company_name"
                  value={formData.company_name}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 input-industrial border"
                  data-testid="form-company-name"
                />
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-medium text-slate-300 mb-2">First Name *</label>
                  <input
                    type="text"
                    name="first_name"
                    value={formData.first_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 input-industrial border"
                    data-testid="form-first-name"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-slate-300 mb-2">Last Name *</label>
                  <input
                    type="text"
                    name="last_name"
                    value={formData.last_name}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 input-industrial border"
                    data-testid="form-last-name"
                  />
                </div>
              </div>

              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-base font-medium text-slate-300 mb-2">Phone *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 input-industrial border"
                    data-testid="form-phone"
                  />
                </div>
                <div>
                  <label className="block text-base font-medium text-slate-300 mb-2">Email *</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 input-industrial border"
                    data-testid="form-email"
                  />
                </div>
              </div>

              <div>
                <label className="block text-base font-medium text-slate-300 mb-2">Type of Business *</label>
                <select
                  name="business_type"
                  value={formData.business_type}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 input-industrial border"
                  data-testid="form-business-type"
                >
                  <option value="home">Home</option>
                  <option value="commercial">Commercial</option>
                  <option value="other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-base font-medium text-slate-300 mb-2">Job Title *</label>
                <input
                  type="text"
                  name="job_title"
                  value={formData.job_title}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 input-industrial border"
                  data-testid="form-job-title"
                />
              </div>

              <div>
                <label className="block text-base font-medium text-slate-300 mb-2">Comments</label>
                <textarea
                  name="comments"
                  value={formData.comments}
                  onChange={handleChange}
                  rows="4"
                  className="w-full px-4 py-3 input-industrial border resize-none"
                  placeholder="Tell us about your project..."
                  data-testid="form-comments"
                />
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="form-submit"
              >
                {isSubmitting ? 'Submitting...' : 'Submit Request'}
              </button>

              {submitMessage && (
                <p
                  className={`text-center mt-4 ${
                    submitMessage.includes('Error') ? 'text-red-500' : 'text-green-500'
                  }`}
                  data-testid="form-message"
                >
                  {submitMessage}
                </p>
              )}
            </form>
          </div>

          {/* Contact Info & Map */}
          <div className="space-y-8">
            {/* Contact Details */}
            <div className="glass-card p-8" data-testid="contact-info">
              <h3
                className="text-2xl font-semibold mb-6 uppercase text-orange-500"
                style={{ fontFamily: 'Rajdhani, sans-serif' }}
              >
                3D Metal Home Building
              </h3>
              
              <div className="space-y-4">
                <div className="flex items-start space-x-3">
                  <MapPin className="text-orange-500 flex-shrink-0 mt-1" size={20} />
                  <div>
                    <p className="text-lg text-slate-300 leading-relaxed">
                      One Galleria Tower<br />
                      13355 Noel Rd. Suite 1100<br />
                      Dallas, TX 75240
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <Phone className="text-orange-500" size={20} />
                  <a href="tel:9729728989" className="text-lg text-slate-300 hover:text-orange-500 transition-colors">
                    972-972-8989
                  </a>
                </div>

                <div className="flex items-center space-x-3">
                  <Mail className="text-orange-500" size={20} />
                  <a href="mailto:sales@3dmetalhomebuilding.com" className="text-lg text-slate-300 hover:text-orange-500 transition-colors">
                    sales@3dmetalhomebuilding.com
                  </a>
                </div>

                <div className="flex items-center space-x-3">
                  <Globe className="text-orange-500" size={20} />
                  <span className="text-lg text-slate-300">3DMETALHOMEBUILDING.COM</span>
                </div>
              </div>
            </div>

            {/* Map */}
            <div className="glass-card p-2 overflow-hidden" data-testid="contact-map">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3349.8707726584894!2d-96.82149892395456!3d32.91866647362098!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x864c2155e8b6e8c9%3A0x7c9c8e8d8f8f8f8f!2s13355%20Noel%20Rd%20Suite%201100%2C%20Dallas%2C%20TX%2075240!5e0!3m2!1sen!2sus!4v1234567890"
                width="100%"
                height="300"
                style={{ border: 0 }}
                allowFullScreen=""
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                className="rounded"
              ></iframe>
            </div>
          </div>
        </div>
      </div>
    </section>
    </>
  );
};

export default ContactSection;
